import type { VoteType } from "@rallly/database";
import { TrashIcon } from "lucide-react";
import React from "react";
import { useTranslation } from "@/i18n/client";
import { dayjs } from "@/lib/dayjs";
import type { GetPollApiResponse } from "@/trpc/client/types";
import type {
  ParsedDateOption,
  ParsedTimeSlotOption,
} from "@/utils/date-time-utils";
import { getDuration } from "@/utils/date-time-utils";
import { useDayjs } from "@/utils/dayjs";

import { useParticipants } from "./participants-provider";
import { useRequiredContext } from "./use-required-context";

type PollContextValue = {
  poll: GetPollApiResponse;
  highScore: number;
  optionIds: string[];
  getScore: (optionId: string) => {
    yes: number;
    ifNeedBe: number;
    no: number;
    skip: number;
  };
  getVote: (participantId: string, optionId: string) => VoteType | undefined;
};

export const PollContext = React.createContext<PollContextValue | null>(null);

PollContext.displayName = "PollContext.Provider";

export const usePoll = () => {
  const context = useRequiredContext(PollContext);
  return context;
};

export const PollContextProvider: React.FunctionComponent<{
  poll: GetPollApiResponse;
  children?: React.ReactNode;
}> = ({ poll, children }) => {
  const { t } = useTranslation();
  const { participants } = useParticipants();

  const getScore = React.useCallback(
    (optionId: string) => {
      return (participants ?? []).reduce(
        (acc, curr) => {
          curr.votes.forEach((vote) => {
            if (vote.optionId !== optionId) {
              return;
            }
            if (vote.type === "yes") {
              acc.yes += 1;
            } else if (vote.type === "ifNeedBe") {
              acc.ifNeedBe += 1;
            } else if (vote.type === "no") {
              acc.no += 1;
            } else {
              acc.skip += 1;
            }
          });
          return acc;
        },
        { yes: 0, ifNeedBe: 0, no: 0, skip: 0 },
      );
    },
    [participants],
  );

  const contextValue = React.useMemo<PollContextValue>(() => {
    const highScore = poll.options.reduce((acc, curr) => {
      const { yes, ifNeedBe } = getScore(curr.id);
      const score = yes + ifNeedBe;
      return score > acc ? score : acc;
    }, 1);

    const optionIds = poll.options.map(({ id }) => id);

    return {
      optionIds,
      poll,
      highScore,
      getVote: (participantId, optionId) => {
        const vote = participants
          .find((participant) => participant.id === participantId)
          ?.votes.find((vote) => vote.optionId === optionId);
        return vote?.type;
      },
      getScore,
    };
  }, [getScore, participants, poll]);

  if (poll.deleted) {
    return (
      <div className="flex h-[calc(100vh-100px)] w-full items-center justify-center">
        <div className="space-y-4 text-center">
          <TrashIcon className="mb-4 inline-block size-24 text-muted-foreground" />
          <div className="mb-2 font-bold text-3xl text-foreground">
            {t("deletedPoll")}
          </div>
          <p className="text-muted-foreground">{t("deletedPollInfo")}</p>
        </div>
      </div>
    );
  }
  return (
    <PollContext.Provider value={contextValue}>{children}</PollContext.Provider>
  );
};

type OptionsContextValue =
  | {
      pollType: "date";
      options: ParsedDateOption[];
    }
  | {
      pollType: "timeSlot";
      options: ParsedTimeSlotOption[];
    };

const OptionsContext = React.createContext<OptionsContextValue>({
  pollType: "date",
  options: [],
});

export const useOptions = () => {
  const context = React.useContext(OptionsContext);
  return context;
};

export function createOptionsContextValue(
  pollOptions: { id: string; startTime: Date; duration: number }[],
  targetTimeZone: string,
  sourceTimeZone: string | null,
): OptionsContextValue {
  if (pollOptions.length === 0) {
    return {
      pollType: "date",
      options: [],
    };
  }
  if (pollOptions[0].duration > 0) {
    return {
      pollType: "timeSlot",
      options: pollOptions.map((option) => {
        function adjustTimeZone(date: Date) {
          if (sourceTimeZone) {
            return dayjs(date).tz(targetTimeZone);
          }
          return dayjs(date).utc();
        }
        const localStartTime = adjustTimeZone(option.startTime);

        // for some reason, dayjs requires us to do timezone conversion at the end
        const localEndTime = adjustTimeZone(
          dayjs(option.startTime).add(option.duration, "minute").toDate(),
        );

        return {
          optionId: option.id,
          type: "timeSlot",
          startTime: localStartTime.format("LT"),
          endTime: localEndTime.format("LT"),
          duration: getDuration(localStartTime, localEndTime),
          month: localStartTime.format("MMM"),
          day: localStartTime.format("D"),
          dow: localStartTime.format("ddd"),
          year: localStartTime.format("YYYY"),
        } satisfies ParsedTimeSlotOption;
      }),
    };
  } else {
    return {
      pollType: "date",
      options: pollOptions.map((option) => {
        // All-day options are floating dates: always read them in UTC so the
        // calendar date is identical for every viewer, regardless of timezone.
        const localTime = dayjs(option.startTime).utc();

        return {
          optionId: option.id,
          type: "date",
          month: localTime.format("MMM"),
          day: localTime.format("D"),
          dow: localTime.format("ddd"),
          year: localTime.format("YYYY"),
        } satisfies ParsedDateOption;
      }),
    };
  }
}

export const OptionsProvider = (props: React.PropsWithChildren) => {
  const { poll } = usePoll();
  const { timeZone: targetTimeZone, timeFormat } = useDayjs();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Fix this later
  const options = React.useMemo(() => {
    return createOptionsContextValue(
      poll.options,
      targetTimeZone,
      poll.timeZone,
    );
  }, [poll.options, poll.timeZone, targetTimeZone, timeFormat]);

  return (
    <OptionsContext.Provider value={options}>
      {props.children}
    </OptionsContext.Provider>
  );
};
